import { AxonEvent } from './axon_event';
import { HttpConnection } from './http_connection';
import { SocketController } from './socket_controller';
import { Reporter } from './_reporter';
import type { Response } from './common';

/**
 * Module responsible for signaling messages to and from controllers
 */
export class ControllerContext {
	private readonly controllers = new Map<string, SocketController>();

	public static create(httpConnection: HttpConnection, reporter: Reporter): ControllerContext {
		return new ControllerContext(httpConnection, reporter);
	}

	private constructor(
		public readonly httpConnection: HttpConnection,
		private readonly reporter: Reporter,
	) {
		// Empty
	}

	public get(ref: string): SocketController {
		let instance = this.controllers.get(ref);

		if (instance == null) {
			instance = this.add(ref);
		}

		return instance;
	}

	public add(ref: string): SocketController {
		this.reporter.log(`Connects to controller: ${ref}`);
		this.remove(ref);

		const instance = new SocketController(ref, this, this.reporter);
		this.controllers.set(ref, instance);
		instance.publish(AxonEvent.Init);

		return instance;
	}

	public remove(ref: string): void {
		if (this.controllers.has(ref)) {
			this.controllers.delete(ref);
		}
	}

	public panicControllers(): void {
		for (const controllers of this.controllers.values()) {
			controllers.communicateReconnect();
		}
	}

	public calmControllers(): void {
		for (const controllers of this.controllers.values()) {
			controllers.communicateReconnected();
		}
	}

	public resyncUncoupledControllers(): void {
		for (const [key, value] of this.controllers.entries()) {
			// The ping is an exception to this resync
			if (key === 'ping') continue;

			value.publish(AxonEvent.Init);
		}
	}

	public handleControllerMessage(message: Response): void {
		if (message.topic === AxonEvent.Error) {
			this.reporter.log(`Controller message carried an error: ${message.data}`);

			return;
		}
		if (message.topic === AxonEvent.AuthFailed) {
			return;
		}

		const controllerInstance = this.controllers.get(message.controller) ?? this.add(message.controller);
		if (controllerInstance) {
			controllerInstance.dispatchEvent(message);
		}
	}
}
